// Static booth layout & category definitions for IJFF 2026
// Coordinates use viewBox: 1400 x 900 (SVG interactive map)

export const CATEGORIES = {
  event_management:   { key: "event_management",   label: "Event Management",     shortLabel: "EVENT",    color: "#1A237E", count: 3,  size: "3x3m" },
  info_volunteer:     { key: "info_volunteer",     label: "Information & Volunteer", shortLabel: "INFO", color: "#0EA5E9", count: 2,  size: "3x3m" },
  indonesian_embassy: { key: "indonesian_embassy", label: "Indonesian Embassy",   shortLabel: "KBRI",     color: "#B22222", count: 12, size: "3x3m" },
  platinum_sponsor:   { key: "platinum_sponsor",   label: "Platinum Sponsor (Remittance)", shortLabel: "PLAT", color: "#94A3B8", count: 6, size: "3x3m" },
  gold_sponsor:       { key: "gold_sponsor",       label: "Gold Sponsor",         shortLabel: "GOLD",     color: "#D4AF37", count: 8,  size: "3x3m" },
  silver_sponsor:     { key: "silver_sponsor",     label: "Silver Sponsor",       shortLabel: "SLV",      color: "#94A3B8", count: 18, size: "3x3m" },
  bronze_sponsor:     { key: "bronze_sponsor",     label: "Bronze Sponsor",       shortLabel: "BRZ",      color: "#B87333", count: 20, size: "3x3m" },
  restaurant:         { key: "restaurant",         label: "Restaurant / Catering", shortLabel: "FOOD",    color: "#E65100", count: 30, size: "3x3m + facilities" },
  trader:             { key: "trader",             label: "Product Traders",      shortLabel: "TRD",      color: "#8D6E63", count: 15, size: "3x3m" },
  local_government:   { key: "local_government",   label: "Local Government",     shortLabel: "GOV",      color: "#2E7D32", count: 2,  size: "3x3m" },
  craft_fashion:      { key: "craft_fashion",      label: "Craft & Fashion & Merch", shortLabel: "CRFT",  color: "#7C3AED", count: 10, size: "3x3m" },
  business_matching:  { key: "business_matching",  label: "Business Matching Tent", shortLabel: "B-MATCH", color: "#0277BD", count: 1, size: "6 x 18 m" },
};

export const AMENITIES = [
  { type: "stage", label: "Main Stage",          x: 200,  y: 380, w: 220, h: 140, icon: "🎤" },
  { type: "entrance", label: "Entrance Gate",   x: 1290, y: 420, w: 60,  h: 60,  icon: "IN" },
  { type: "exit",     label: "Exit Gate",       x: 1290, y: 520, w: 60,  h: 60,  icon: "OUT" },
  { type: "toilet",   label: "Permanent Toilet", x: 60,  y: 640, w: 70,  h: 45,  icon: "WC" },
  { type: "toilet",   label: "Portable Toilet",  x: 1180, y: 720, w: 70,  h: 45,  icon: "WC" },
  { type: "smoking",  label: "Smoking Area",     x: 60,   y: 720, w: 70,  h: 45,  icon: "🚬" },
  { type: "rubbish",  label: "Rubbish / Gomi Station", x: 720, y: 730, w: 100, h: 45, icon: "♻" },
  { type: "changing", label: "Changing Room",    x: 260, y: 550,  w: 90,  h: 40,  icon: "CHG" },
  { type: "organizer", label: "Organizer Area",  x: 380, y: 550,  w: 100, h: 40,  icon: "ORG" },
];

// Build 100+ booths across the venue.
// Layout mapped roughly on the Shiokaze Park oval floor plan:
//  - Business Matching big tent: top-left
//  - Sponsors block: center-top
//  - Embassy block: right-top
//  - Traders + Craft + Gov: middle rows
//  - Restaurant arc: bottom curve
//  - Event Mgmt / Info: near organizer area

function buildBooths() {
  const booths = [];
  let seq = 1;

  const add = (category, x, y, w = 44, h = 44, label) => {
    booths.push({
      id: `B${String(seq).padStart(3, "0")}`,
      seq,
      category,
      x, y, w, h,
      label: label || `${CATEGORIES[category].shortLabel} ${seq}`,
    });
    seq++;
  };

  // ---- Business Matching big tent (6x18m) ---- one large tent
  booths.push({
    id: `BM01`,
    seq: 0,
    category: "business_matching",
    x: 490, y: 130, w: 260, h: 90,
    label: "Business Matching Tent (6×18m)",
    isBig: true,
  });

  // ---- Event Management (3) near organizer area
  add("event_management", 500, 555, 44, 40);
  add("event_management", 548, 555, 44, 40);
  add("event_management", 596, 555, 44, 40);

  // ---- Info & Volunteer (2) near entrance
  add("info_volunteer", 1180, 470, 44, 40);
  add("info_volunteer", 1180, 515, 44, 40);

  // ---- Indonesian Embassy (12) — grid 6x2
  const embX0 = 800, embY0 = 300;
  for (let r = 0; r < 2; r++) {
    for (let c = 0; c < 6; c++) {
      add("indonesian_embassy", embX0 + c * 50, embY0 + r * 50);
    }
  }

  // ---- Platinum Sponsor (6) — one remittance co. — row above embassy
  for (let c = 0; c < 6; c++) {
    add("platinum_sponsor", 800 + c * 50, 240);
  }

  // ---- Gold Sponsor (8) — two participants @4 booths each — center-top-left
  for (let c = 0; c < 4; c++) add("gold_sponsor", 520 + c * 50, 240);
  for (let c = 0; c < 4; c++) add("gold_sponsor", 520 + c * 50, 285);

  // ---- Silver Sponsor (18) — 6 participants @3 booths = 18 — right cluster row
  for (let c = 0; c < 9; c++) add("silver_sponsor", 520 + c * 50, 335);
  for (let c = 0; c < 9; c++) add("silver_sponsor", 520 + c * 50, 380);

  // ---- Bronze Sponsor (20) — 10 participants @2 booths = 20 — mid row
  for (let c = 0; c < 10; c++) add("bronze_sponsor", 520 + c * 50, 430);
  for (let c = 0; c < 10; c++) add("bronze_sponsor", 520 + c * 50, 475);

  // ---- Product Traders (15)
  for (let c = 0; c < 8; c++) add("trader", 800 + c * 50, 410);
  for (let c = 0; c < 7; c++) add("trader", 800 + c * 50, 455);

  // ---- Craft & Fashion (10)
  for (let c = 0; c < 5; c++) add("craft_fashion", 60 + c * 50, 230);
  for (let c = 0; c < 5; c++) add("craft_fashion", 60 + c * 50, 275);

  // ---- Local Government (2)
  add("local_government", 60, 340);
  add("local_government", 110, 340);

  // ---- Restaurant/Catering (30) — arranged as arc along bottom curve
  const cx = 700, cy = 680; // center
  const rx = 620, ry = 200;
  for (let i = 0; i < 30; i++) {
    // angle from 200deg to 340deg (bottom arc)
    const angle = (Math.PI * (200 + (140 * i) / 29)) / 180;
    const bx = cx + rx * Math.cos(angle) - 22;
    const by = cy + ry * Math.sin(angle) - 22;
    add("restaurant", bx, by);
  }

  return booths;
}

export const BOOTHS = buildBooths();

// Sponsor tier scheme — matches the official IJFF 2026 sponsor proposal PDF
export const SPONSOR_TIERS = [
  {
    key: "co_partner",
    name: "Co-Partner",
    priceJpy: "8,000,000",
    color: "#0EA5E9",
    booths: "8 booths (CB-1 / CB-2 / CB-3 — 3 slots)",
    perks: [
      "Opening ceremony stage appearance + VIP photo session",
      "Daily brand activation on main stage (3–5 min/day)",
      "30-second promo video on backdrop during intermissions",
      "Highest logo exposure (3L) — gate, stage, LED, banners, ID cards",
      "Branding on food court tables & gate decoration",
      "Logo on tent fascia signage",
      "MC introduction + stage PR opportunity (1× per day)",
      "Corporate merchandise (ecobag, beanie, bandana, lanyard, pouch, cap)",
      "Official festival T-shirt & hall of fame",
      "Photobooth with all sponsors & booths",
      "Certificate & Event Report",
    ],
    note: "3 Co-Partner Booth slots available (CB-1, CB-2, CB-3)",
  },
  {
    key: "platinum",
    name: "Platinum",
    priceJpy: "4,000,000",
    color: "#94A3B8",
    booths: "4 booths",
    perks: [
      "Mid-event stage appearance",
      "Sub-stage performance slot (3–5 min)",
      "30-second promo video on backdrop during intermissions",
      "2L logo exposure — gate, stage, LED, official website",
      "Branding on food court tables & gate decoration",
      "Logo on tent fascia signage & staff ID cards",
      "MC introduction + stage PR opportunity",
      "Corporate merchandise + Official festival T-shirt",
      "Photobooth & hall of fame",
      "Certificate & Event Report",
    ],
    note: "4 participant slots available",
  },
  {
    key: "gold",
    name: "Gold",
    priceJpy: "3,000,000",
    color: "#D4AF37",
    booths: "3 booths",
    perks: [
      "3 MC introductions during event",
      "L logo exposure — gate, stage sleeves, LED, website",
      "Logo posting on social media & flyers",
      "Branding placement at food court tables",
      "Space for branding at gate decoration",
      "Corporate merchandise + Official festival T-shirt",
      "Photobooth & hall of fame",
      "Certificate & Event Report",
    ],
    note: "5 participant slots available",
  },
  {
    key: "silver",
    name: "Silver",
    priceJpy: "1,000,000",
    color: "#94A3B8",
    booths: "2 booths",
    perks: [
      "2 MC introductions during event",
      "M logo exposure — gate, stage sleeves, LED, website",
      "Logo posting on social media & flyers",
      "Branding placement at food court tables",
      "Space for branding at gate decoration",
      "Corporate merchandise + Official festival T-shirt",
      "Photobooth & hall of fame",
      "Certificate & Event Report",
    ],
    note: "8 participant slots available",
  },
];

export const BOOTH_TYPES = [
  {
    key: "food",
    name: "Food Booth",
    nameJa: "飲食ブース",
    priceJpy: "330,000",
    size: "3 × 3 m",
    equipment: ["1 tent 3×3m", "2 tables", "4 chairs", "1 sink w/ clean water", "1 gas burner point", "Small refrigerator", "Power outlet"],
    image: "https://images.unsplash.com/photo-1535898331935-2d274aff0fbc",
  },
  {
    key: "catering",
    name: "Catering Booth",
    nameJa: "ケータリングブース",
    priceJpy: "242,000",
    size: "3 × 3 m",
    equipment: ["1 tent 3×3m", "2 tables", "4 chairs", "Handwash station", "Power outlet"],
    image: "https://images.pexels.com/photos/12657546/pexels-photo-12657546.jpeg",
  },
  {
    key: "exhibition",
    name: "Exhibition Booth",
    nameJa: "物販・展示ブース",
    priceJpy: "264,000",
    size: "3 × 3 m",
    equipment: ["1 tent 3×3m (red/white)", "2 tables", "4 chairs", "Power outlet"],
    image: "https://customer-assets.emergentagent.com/job_f15d1a1b-1b99-454c-90f2-2714dda00985/artifacts/l17r75y6_Stage%20planning%20design.jpeg",
  },
  {
    key: "bring_in",
    name: "Bring-In Exhibition",
    nameJa: "物販持込ブース",
    priceJpy: "198,000",
    size: "3 × 3 m (space only)",
    equipment: ["Booth space only — bring your own tent/table"],
    image: "https://images.pexels.com/photos/35243199/pexels-photo-35243199.jpeg",
  },
  {
    key: "business_matching",
    name: "Business Matching Tent",
    nameJa: "ビジネスマッチングテント",
    priceJpy: "By registration",
    size: "6 × 18 m (shared)",
    equipment: ["Shared large covered tent", "Meeting tables for 50–100 pax", "Sound system", "Interpreter desk"],
    image: "https://customer-assets.emergentagent.com/job_f15d1a1b-1b99-454c-90f2-2714dda00985/artifacts/df73eeev_the%20situation%20of%20business%20batching.jpeg",
  },
];
